import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { REACT_APP_API_URL } from "../../../env";
const GENERIC_AUTH_ERROR = "Invalid credentials";
const initialState = {
  user: JSON.parse(localStorage.getItem('authUser')) || {},
  loginResponse: null,
  loading: false,
  error: null,
  isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
  userType: localStorage.getItem('userType') || null, // 'admin' or 'hrms'
  hrmsUser: JSON.parse(localStorage.getItem('hrmsUser')) || null,
  permissions: JSON.parse(localStorage.getItem('permissions')) || []
};

axios.defaults.withCredentials = true;

export const fetchUserDetails = createAsyncThunk(
  'auth/fetchUserDetails',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userType = state.auth?.userType || localStorage.getItem('userType');

      const profileUrl = userType === 'hrms'
        ? `${REACT_APP_API_URL}api/hrms/auth/employee-profile`
        : `${REACT_APP_API_URL}api/dashboard/admin/profile`;

      const response = await axios.get(profileUrl, {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || 'Something went wrong',
        status: error.response?.status
      });
    }
  }
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (
    { username, password, captchaId, captchaValue },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/dashboard/admin/login`,
        {
          Username: username,
          User_Password: password,
          captchaId,
          captchaValue,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      if (error.code === "ECONNABORTED") {
        return rejectWithValue("Request timed out. Please try again.");
      }

      const backendMessage = error.response?.data?.message;

      if (backendMessage?.toLowerCase().includes("captcha")) {
        return rejectWithValue(backendMessage);
      }
      return rejectWithValue(GENERIC_AUTH_ERROR);
    }
  }
);

// HRMS specific login using separate API base
export const loginHrmsUser = createAsyncThunk(
  "auth/loginHrmsUser",
  async (
    { employee_id, password, captchaId, captchaValue },
    { rejectWithValue }
  ) => {
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}api/hrms/auth/login`,
        {
          employee_id,
          password,
          captchaId,
          captchaValue,
        },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          timeout: 10000,
        }
      );

      return response.data;
    } catch (error) {
      // Timeout handling
      if (error.code === "ECONNABORTED") {
        return rejectWithValue("Request timed out. Please try again.");
      }

      const backendMessage = error.response?.data?.message;

      // Explicit captcha errors
      if (
        backendMessage &&
        backendMessage.toLowerCase().includes("captcha")
      ) {
        return rejectWithValue(backendMessage);
      }

      return rejectWithValue(GENERIC_AUTH_ERROR);
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const userType = state.auth.userType;

      const logoutUrl = userType === 'hrms'
        ? `${REACT_APP_API_URL}api/hrms/auth/logout`
        : `${REACT_APP_API_URL}api/dashboard/admin/logout`;

      await axios.post(logoutUrl, {}, {
        withCredentials: true
      });
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Logout failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    rehydrateAuthState(state) {
      state.user = JSON.parse(localStorage.getItem('authUser')) || {};
      state.isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
      state.userType = localStorage.getItem('userType') || null;
      state.hrmsUser = JSON.parse(localStorage.getItem('hrmsUser')) || null;
      state.permissions = JSON.parse(localStorage.getItem('permissions')) || [];
    },

    updateHrmsUser(state, action) {
      state.hrmsUser = {
        ...state.hrmsUser,
        ...action.payload,
      };
      localStorage.setItem('hrmsUser', JSON.stringify(state.hrmsUser));
    }
  },

  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userType', 'admin');
        localStorage.removeItem('hrmsUser');
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('hrmsLogin');
        sessionStorage.removeItem('hrmsUser');

        const permissions = action.payload?.permissions || [];
        localStorage.setItem('permissions', JSON.stringify(permissions));
        state.permissions = permissions;

        state.isAuthenticated = true;
        state.userType = 'admin';
        state.hrmsUser = null;
        state.loginResponse = action.payload;
      })

      .addCase(loginHrmsUser.fulfilled, (state, action) => {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userType', 'hrms');
        localStorage.setItem('hrmsUser', JSON.stringify(action.payload.employee));
        localStorage.removeItem('authUser');
        localStorage.removeItem('authToken');
        sessionStorage.setItem('hrmsLogin', 'true');
        sessionStorage.setItem('hrmsUser', JSON.stringify(action.payload));

        const permissions = action.payload?.permissions || [];
        localStorage.setItem('permissions', JSON.stringify(permissions));
        state.permissions = permissions;

        state.isAuthenticated = true;
        state.userType = 'hrms';
        state.user = {};
        state.hrmsUser = action.payload.employee;
        state.loginResponse = action.payload;
      })

      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        if (state.userType === 'hrms') {
          const employee = action.payload?.employee || action.payload;
          localStorage.setItem('hrmsUser', JSON.stringify(employee));
          state.hrmsUser = employee;
        } else {
          localStorage.setItem('authUser', JSON.stringify(action.payload));
          state.user = action.payload;
        }
        state.isAuthenticated = true;
      })

      .addCase(fetchUserDetails.rejected, (state, action) => {
        // clear auth state if it's a 401 (unauthorized) response
        const status = action.payload?.status || action.payload?.statusCode;
        const is401 = status === 401 || (typeof action.payload === 'string' && action.payload.includes('401'));

        if (is401) {
          localStorage.removeItem('authUser');
          localStorage.removeItem('hrmsUser');
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('userType');
          localStorage.removeItem('authToken');
          localStorage.removeItem('permissions');
          sessionStorage.removeItem('hrmsLogin');
          sessionStorage.removeItem('hrmsUser');
          state.user = {};
          state.hrmsUser = null;
          state.permissions = [];
          state.userType = null;
          state.isAuthenticated = false;
        } else {
          console.warn('fetchUserDetails failed (non-401), keeping auth state:', action.payload);
        }
      })

      .addCase(logoutUser.fulfilled, (state) => {
        localStorage.clear();
        sessionStorage.clear();
        state.user = {};
        state.isAuthenticated = false;
        state.userType = null;
        state.hrmsUser = null;
        state.permissions = [];
      });

    builder.addCase(logoutUser.rejected, (state) => {
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch (e) {
        console.error('Error clearing storage:', e);
      }
      state.user = {};
      state.isAuthenticated = false;
      state.userType = null;
      state.hrmsUser = null;
      state.permissions = [];
    });
  }
});

export const { rehydrateAuthState, updateHrmsUser } = authSlice.actions;
export default authSlice.reducer;